import { EVENTS, ORGANIZATIONS_LOGO_URL, ROUTES } from 'shared/constants';
import { LogoWhite } from 'src/images';
import { Modal, Input, message } from 'antd';
import { useUnlockOrganization } from 'shared/hooks/auth.hooks';
import { redirectToLoginWithSSO } from 'src/helpers/sso';
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faUserPlus, faSearch } from '@fortawesome/pro-regular-svg-icons';
import styles from './switch-organization-dropdown.module.scss';
import OAuthConfirm from './oauth-confirmation-modal';
import OTPConfirm from './otp-confirmation-modal';
import authenticationServiceApi from 'shared/api/authentication.service.api';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'src/components/design-system';
import { sortByAlphabet } from 'shared/utils';
import { useConversion } from 'src/components/knowledge-content/smart-format-conversion.context';
import { useSelector } from 'react-redux';
import { AUTH_USER_TYPE_NORMAL } from 'shared/constants';
import analyticsService from 'src/helpers/analytics.service';

const SwitchOrganizationDropdownComponent = ({
  activeOrganization,
  organizations = [],
  switchOrganization,
  lang,
  selectorLang,
}) => {
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [oAuthModalOpen, setOAuthModalOpen] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchInputRef = useRef(null);
  const user = useSelector(state => state.auth?.user);
  const { getActiveConversions, clearConversions } = useConversion();

  const unlockWithOauth = useCallback(() => setOAuthModalOpen(true), []);
  const unlockWithOtp = useCallback(() => setOtpModalOpen(true), []);
  const unlockOrganization = useUnlockOrganization({
    redirectToSSO: redirectToLoginWithSSO,
    unlockWithOauth,
    unlockWithOtp,
  });

  // Focus search input when dropdown opens
  useEffect(() => {
    if (dropdownOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [dropdownOpen]);

  const filteredOrganizations = useMemo(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();
    if (!trimmedQuery) {
      return organizations;
    }

    return organizations.filter(org => {
      const nameMatches = org.name?.toLowerCase().includes(trimmedQuery);
      const idMatches = trimmedQuery.length >= 5 && org.id?.toLowerCase().includes(trimmedQuery);
      return nameMatches || idMatches;
    });
  }, [organizations, searchQuery]);

  const items = useMemo(
    () => [
      ...filteredOrganizations
        .sort((a, b) => sortByAlphabet(a.name, b.name))
        .map(org => ({
          // No nested role here: antd already renders the <li role="menuitem">, and a second
          // one made the org name ambiguous to assistive tech and to `getByRole`.
          label: !org.activated ? (
            <span>
              <FontAwesomeIcon
                icon={faLock}
                className={styles.menuIcon}
                title={selectorLang.LOCKED_ORGANIZATION}
              />
              {org.name}
            </span>
          ) : (
            org.name
          ),
          key: org.id,
        })),
      {
        label: (
          <>
            <FontAwesomeIcon icon={faUserPlus} className={styles.menuIcon} />
            {lang.MENU_TITLE}
          </>
        ),
        key: 'add',
        style: {
          fontWeight: 'bold',
        },
        onClick: () => {
          setOtpModalOpen(true);
        },
        hide: user?.type !== AUTH_USER_TYPE_NORMAL,
      },
    ],
    [filteredOrganizations, lang.MENU_TITLE, user.type, selectorLang.LOCKED_ORGANIZATION],
  );

  const doSwitchOrganization = useCallback(() => {
    (async function () {
      if (organization.activated) {
        analyticsService.track(EVENTS.ORGANIZATION.SWITCH, {
          sourceOrganizationId: activeOrganization.id,
          targetOrganizationId: organization.id,
        });
        const { error } = await switchOrganization({ id: organization.id });
        if (error) {
          message.error(selectorLang.SWITCH_FAILED);
          setOrganization(organizations.find(org => org.id === activeOrganization.id));
          return;
        }
        // Profile-completion popup is triggered by user.middleware on ORGANIZATION_SWITCH
        navigate(ROUTES.HOME);
        return;
      }

      await unlockOrganization(organization);
    })();
  }, [
    switchOrganization,
    organization,
    organizations,
    navigate,
    activeOrganization.id,
    unlockOrganization,
    selectorLang.SWITCH_FAILED,
  ]);

  React.useEffect(() => {
    if (organization && organization.id !== activeOrganization.id) {
      doSwitchOrganization();
    }
  }, [organization, doSwitchOrganization, activeOrganization.id]);

  const onSubmitEmail = async email => {
    return await authenticationServiceApi.activateOTPAccount({
      email,
      organizationId: organization?.id,
    });
  };

  const onVerifyCode = async ({ email, code }) => {
    await authenticationServiceApi.verifyActivateAccount({
      email,
      code,
      organizationId: organization?.id,
    });

    if (organization?.id) {
      switchOrganization({ id: organization?.id });
    }
  };

  return (
    <>
      <Modal
        open={oAuthModalOpen}
        onCancel={() => {
          setOAuthModalOpen(false);
          setOrganization(organizations.find(org => org.id === activeOrganization.id));
        }}
        width={700}
        footer={null}
        centered
        maskClosable={false}
        destroyOnHidden>
        <OAuthConfirm organization={organization} />
      </Modal>
      <Modal
        open={otpModalOpen}
        onCancel={() => {
          setOtpModalOpen(false);
          setOrganization(organizations.find(org => org.id === activeOrganization.id));
        }}
        width={700}
        footer={null}
        centered
        maskClosable={false}
        destroyOnHidden>
        <OTPConfirm submitEmailCallback={onSubmitEmail} verifyCodeCallback={onVerifyCode} />
      </Modal>

      <Dropdown
        aria-label="organization selector"
        items={items}
        trigger={['click']}
        onSelect={async key => {
          const organization = organizations.find(org => org.id === key);
          const activeConversions = getActiveConversions();

          if (activeConversions > 0) {
            Modal.confirm({
              title: lang.CONVERSIONS_ONGOING_TITLE,
              content: lang.CONVERSIONS_ONGOING_MESSAGE,
              okText: lang.CONVERSIONS_ONGOING_CONFIRM,
              okButtonProps: { type: 'primary', danger: true },
              cancelText: lang.CONVERSIONS_ONGOING_CANCEL,
              onOk: () => {
                clearConversions();
                setOrganization(organization);
              },
            });
          } else {
            setOrganization(organization);
          }
        }}
        activeKey={activeOrganization.id}
        showArrowIcon
        bordered
        childrenWrapperStyle={styles.dropdownInnerWrapper}
        popupRender={menu => (
          <div className={styles.dropdownMenuWrapper}>
            {organizations.length > 5 && (
              <div className={styles.searchContainer}>
                <Input
                  ref={searchInputRef}
                  placeholder="Search by name or ID..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  prefix={<FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />}
                  className={styles.searchInput}
                  allowClear
                />
              </div>
            )}
            {filteredOrganizations.length === 0 && searchQuery.trim() ? (
              <div className={styles.emptyState}>No organizations found</div>
            ) : (
              menu
            )}
          </div>
        )}
        onOpenChange={open => {
          setDropdownOpen(open);
          // Clear search when dropdown closes
          if (!open) {
            setSearchQuery('');
          }
        }}>
        <div className={styles.logoWrapper}>
          {activeOrganization.logo ? (
            <img
              src={ORGANIZATIONS_LOGO_URL + 'web/' + activeOrganization.logo}
              alt={activeOrganization.name}
              className={styles.logo}
            />
          ) : (
            <LogoWhite className={styles.logo} />
          )}
        </div>
      </Dropdown>
    </>
  );
};

export { SwitchOrganizationDropdownComponent };
